import { ApproveAppDialog } from 'app-governance-lib/components/ApproveAppDialog';
import { ApolloProvider } from '@apollo/client';
import { createApolloClient } from 'hubspot-apollo-client';
import { CheckCircleIcon } from 'foundations-assets/icons/components/CheckCircleIcon';
import { CogIcon } from 'foundations-assets/icons/components/CogIcon';
import { Alert } from 'foundations-components/transitional/components/Alert';
import { Button } from 'foundations-components/transitional/components/Button';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { useMemo, useState } from 'react';
import { ApprovalProvider } from 'ui-product-approvals/components/ApprovalProvider';
import { useGetUsersByIdsQuery } from '../../__generated__/chirp/com/hubspot/appusers/rpc/UsersRpc.hooks';
import { useRejectApprovalMutation } from '../../__generated__/chirp/com/hubspot/product/approvals/rpc/ApprovalsRpc.hooks';
import { useDetailsPageContext } from '../context/DetailsPageContext';
import { APP_GOVERNANCE_APPROVAL_ACTION, APP_GOVERNANCE_APPROVAL_TYPE } from './constants';
import { useGetLatestProductApprovalRequest } from './hooks/useGetLatestProductApprovalRequest';
import { useGetOrCreateApprovableObjectId } from './hooks/useGetOrCreateApprovableObjectId';
import { useGetProductApprovalConfiguration } from './hooks/useGetProductApprovalConfiguration';
import { getUserHasAppGovernanceWriteScope } from './utils';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const apolloClient = createApolloClient();
function AppGovernanceAlertContent({
  appId,
  appName,
  approvableObjectId,
  currentUserId
}) {
  var _data$approval, _userData$users$;
  const [isAppGovernanceModalOpen, setIsAppGovernanceModalOpen] = useState(false);
  const {
    data,
    hasLatestRequestData,
    requestStatus,
    requesterId,
    loading,
    refetch
  } = useGetLatestProductApprovalRequest({
    approvableObjectId
  });
  const [rejectApproval] = useRejectApprovalMutation({
    variables: {
      approvalId: (data === null || data === void 0 || (_data$approval = data.approval) === null || _data$approval === void 0 ? void 0 : _data$approval.id) || 0,
      rejectionNote: ''
    }
  });
  const {
    data: userData,
    loading: loadingUserData
  } = useGetUsersByIdsQuery({
    variables: {
      userIds: [parseInt(requesterId || '0', 10)]
    },
    skip: requesterId === undefined
  });
  const requesterEmail = userData === null || userData === void 0 || (_userData$users$ = userData.users[0]) === null || _userData$users$ === void 0 ? void 0 : _userData$users$.email;
  let alertType = 'info';
  let alertTitleKey = 'PENDING';
  switch (requestStatus) {
    case 'APPROVED':
      alertType = 'success';
      alertTitleKey = 'APPROVED';
      break;
    case 'REJECTED':
      alertType = 'warning';
      alertTitleKey = 'REJECTED';
      break;
    default:
      break;
  }
  if (loading || loadingUserData || !hasLatestRequestData) {
    return null;
  }
  function handleOpenAppGovernanceModal() {
    setIsAppGovernanceModalOpen(true);
  }
  function handleDenyRequest() {
    rejectApproval().then(() => refetch()).catch(err => console.error(err));
  }
  function onAppGovernanceModalClosed() {
    setIsAppGovernanceModalOpen(false);
    refetch();
  }
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsxs(Alert, {
      type: alertType,
      titleText: /*#__PURE__*/_jsx(FormattedMessage, {
        message: `detailsPage.appGovernance.alertTitle.${alertTitleKey}`,
        options: {
          email: requesterEmail
        }
      }),
      marginBlockEnd: "500",
      children: [/*#__PURE__*/_jsx(FormattedMessage, {
        message: `detailsPage.appGovernance.alertContent.${alertTitleKey}`
      }), requestStatus === 'APPROVAL_PENDING' ? /*#__PURE__*/_jsxs(_Fragment, {
        children: [/*#__PURE__*/_jsxs(Button, {
          marginInlineStart: "200",
          use: "secondary-alt",
          size: "small",
          onClick: handleOpenAppGovernanceModal,
          children: [/*#__PURE__*/_jsx(CheckCircleIcon, {}), /*#__PURE__*/_jsx(FormattedMessage, {
            message: "detailsPage.appGovernance.approveButton"
          })]
        }), /*#__PURE__*/_jsx(Button, {
          use: "tertiary",
          size: "small",
          onClick: handleDenyRequest,
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "detailsPage.appGovernance.denyButton"
          })
        })]
      }) : /*#__PURE__*/_jsxs(Button, {
        marginInlineStart: "200",
        use: "tertiary",
        size: "small",
        onClick: handleOpenAppGovernanceModal,
        children: [/*#__PURE__*/_jsx(CogIcon, {}), /*#__PURE__*/_jsx(FormattedMessage, {
          message: "detailsPage.appGovernance.manageButton"
        })]
      })]
    }), isAppGovernanceModalOpen && /*#__PURE__*/_jsx(ApolloProvider, {
      client: apolloClient,
      children: /*#__PURE__*/_jsx(ApproveAppDialog, {
        type: "modal",
        appId: appId,
        appName: appName,
        currentUserId: currentUserId,
        onClose: onAppGovernanceModalClosed,
        onConfirm: onAppGovernanceModalClosed
      })
    })]
  });
}
export function AppGovernanceAlert({
  appId,
  appName
}) {
  var _userInfo$user$user_i, _userInfo$user;
  const {
    userInfo
  } = useDetailsPageContext();
  const showAppGovernanceAlert = useMemo(() => getUserHasAppGovernanceWriteScope(userInfo), [userInfo]);
  const {
    approvableObjectId
  } = useGetOrCreateApprovableObjectId({
    appId
  });
  const {
    enabled: isProductApprovalsEnabled
  } = useGetProductApprovalConfiguration();
  if (!isProductApprovalsEnabled || !showAppGovernanceAlert || !approvableObjectId) {
    return null;
  }
  return /*#__PURE__*/_jsx(ApprovalProvider, {
    approvableObjectId: approvableObjectId,
    assetName: appName,
    approvalType: APP_GOVERNANCE_APPROVAL_TYPE,
    approvalAction: APP_GOVERNANCE_APPROVAL_ACTION,
    children: /*#__PURE__*/_jsx(AppGovernanceAlertContent, {
      appId: appId,
      appName: appName,
      approvableObjectId: approvableObjectId,
      currentUserId: (_userInfo$user$user_i = userInfo === null || userInfo === void 0 || (_userInfo$user = userInfo.user) === null || _userInfo$user === void 0 ? void 0 : _userInfo$user.user_id) !== null && _userInfo$user$user_i !== void 0 ? _userInfo$user$user_i : 0
    })
  });
}